import React, { useState } from 'react';
import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
import Divider from '@material-ui/core/Divider';
import IconButton from '@material-ui/core/IconButton';
import TextField from '@material-ui/core/TextField';
import Typography from '@material-ui/core/Typography';
import { makeStyles } from '@material-ui/core/styles';
import AddIcon from '@material-ui/icons/Add';
import EditIcon from '@material-ui/icons/Edit';
import MenuItem from '@material-ui/core/MenuItem';
import { RouterlessModal } from '@/se/components/Modal';
import { useProcedureDelayReason } from './useProcedureDelayReason';
import { DelayReasonPatientRef, resolvePatientProcedure } from './procedureDelay';

type ProcedureDelayReasonRowProps = {
  patientId?: number | string | null;
  patient?: DelayReasonPatientRef | null;
};

const useStyles = makeStyles(theme => ({
  item: {
    display: 'flex',
    gap: theme.spacing(2),
    alignItems: 'center',
  },
  info: {
    flex: 1,
    display: 'flex',
    gap: theme.spacing(1),
    alignItems: 'center',
    [theme.breakpoints.down('md')]: {
      flexDirection: 'column',
      alignItems: 'flex-start',
      gap: theme.spacing(0.5),
    },
  },
  mobileButton: {
    [theme.breakpoints.up('lg')]: {
      display: 'none',
    },
  },
  desktopButton: {
    [theme.breakpoints.down('md')]: {
      display: 'none',
    },
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
  },
  actions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: theme.spacing(1),
  },
}));

const ProcedureDelayReasonRow = ({ patientId, patient }: ProcedureDelayReasonRowProps) => {
  const classes = useStyles();
  const [open, setOpen] = useState(false);
  const [selectedReason, setSelectedReason] = useState('');
  const [otherDescription, setOtherDescription] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const procedure = resolvePatientProcedure(patient);
  const {
    ready,
    isDelayed,
    existing,
    hasRecordedReason,
    reasonLabel,
    existingReasonId,
    existingOtherDescription,
    configuredDelayReasons,
    saveReason,
    saving,
  } = useProcedureDelayReason({
    procedureId: procedure?.id,
    patientId: patientId ?? patient?.id,
    operationRoomId: procedure?.orId,
    startTime: procedure?.startTime,
  });

  if (!ready || (!isDelayed && !existing)) {
    return null;
  }

  const openEditor = () => {
    setSelectedReason(existingReasonId ?? '');
    setOtherDescription(existingOtherDescription ?? '');
    setErrorMessage('');
    setOpen(true);
  };

  const isOther = selectedReason.toLowerCase() === 'other';

  const handleSave = async () => {
    if (!selectedReason || (isOther && otherDescription.trim() === '')) {
      setErrorMessage('Select a delay reason');
      return;
    }
    try {
      await saveReason(selectedReason, isOther ? otherDescription.trim() : undefined);
      setOpen(false);
    } catch (error) {
      console.error('Failed to save delay reason', error);
      setErrorMessage('Failed to save delay reason. Please try again.');
    }
  };

  const actionLabel = hasRecordedReason ? 'Edit' : 'Add';

  return (
    <>
      <Box className={classes.item}>
        <Box className={classes.info}>
          <Typography variant="body2">OR Delay Reason</Typography>
          <Typography variant="body2" color="textSecondary">
            {reasonLabel || 'No reason recorded'}
          </Typography>
        </Box>
        <IconButton color="primary" onClick={openEditor} className={classes.mobileButton} size="small">
          {hasRecordedReason ? <EditIcon /> : <AddIcon />}
        </IconButton>
        <Button
          variant="outlined"
          size="small"
          onClick={openEditor}
          startIcon={hasRecordedReason ? <EditIcon /> : <AddIcon />}
          className={classes.desktopButton}
        >
          {actionLabel}
        </Button>
      </Box>
      <Divider />
      {open && (
        <RouterlessModal title={`${actionLabel} OR Delay Reason`} onClose={() => setOpen(false)}>
          <Box className={classes.form}>
            <TextField
              select
              fullWidth
              label="Delay Reason"
              value={selectedReason}
              onChange={event => {
                setSelectedReason(event.target.value);
                setErrorMessage('');
                if (event.target.value.toLowerCase() !== 'other') {
                  setOtherDescription('');
                }
              }}
              variant="outlined"
            >
              <MenuItem value="" disabled>
                Select a reason
              </MenuItem>
              {configuredDelayReasons.map(reason => (
                <MenuItem key={reason.id} value={reason.id}>
                  {reason.label}
                </MenuItem>
              ))}
              {selectedReason &&
                selectedReason.toLowerCase() !== 'other' &&
                !configuredDelayReasons.some(reason => reason.id === selectedReason) && (
                  <MenuItem value={selectedReason}>{selectedReason}</MenuItem>
                )}
            </TextField>
            {isOther && (
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Description"
                value={otherDescription}
                onChange={event => {
                  setOtherDescription(event.target.value);
                  setErrorMessage('');
                }}
                variant="outlined"
              />
            )}
            {errorMessage && (
              <Typography variant="body2" color="error">
                {errorMessage}
              </Typography>
            )}
            <Box className={classes.actions}>
              <Button onClick={() => setOpen(false)} disabled={saving}>
                Cancel
              </Button>
              <Button color="primary" variant="contained" onClick={handleSave} disabled={saving}>
                Save
              </Button>
            </Box>
          </Box>
        </RouterlessModal>
      )}
    </>
  );
};

export default ProcedureDelayReasonRow;
